"use client";

import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { getTrackBackground, Range } from "react-range";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { cn } from "@/lib/cn";
import type { FilterWithFacet } from "../useFacets";
import { useSelectedFilters } from "../useSelectedFilters";

/**
 * A numeric range: two number inputs and a two-thumb slider.
 *
 * The bounds come from the facet — the lowest and highest value any feature in
 * the current scope actually has — rather than from configuration, so a slider
 * over years spans the years the data covers.
 */
export function RangeFilter({
  filter,
  title,
}: {
  filter: FilterWithFacet;
  title: string;
}) {
  const label = title;
  const t = useTranslations();
  const configs = useProjectConfigs();
  const { selectedFilters, setRange, reset } = useSelectedFilters();

  const facet = filter.facet?.type === "range" ? filter.facet : undefined;
  const bounds = useMemo(() => {
    const min = Number(facet?.min);
    const max = Number(facet?.max);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    // A single-valued field would give a zero-width track, which react-range
    // rejects; widening by one keeps the control usable and harmless.
    return min === max ? [min, min + 1] : [Math.floor(min), Math.ceil(max)];
  }, [facet]);

  const selected = selectedFilters[filter.def.name];
  const values = useMemo<[number, number] | null>(() => {
    if (!bounds) return null;
    const [low, high] = bounds;
    const from = Number(selected?.[0]);
    const to = Number(selected?.[1]);
    return [
      Number.isFinite(from) ? Math.max(low, from) : low,
      Number.isFinite(to) ? Math.min(high, to) : high,
    ];
  }, [bounds, selected]);

  const [dragging, setDragging] = useState<number | null>(null);

  if (!bounds || !values) {
    return <p className="p-4 text-sm">{t("search.results.none")}</p>;
  }

  const [low, high] = bounds;
  const commit = (next: number[]) =>
    setRange(filter.def.name, [String(next[0]), String(next[1])]);

  return (
    <div className="p-4">
      <p className="mb-2.5 text-base font-semibold">{title}</p>

      <div className="flex flex-row space-x-2">
        <BoundInput
          value={values[0]}
          min={low}
          max={values[1]}
          unit={filter.def.unit}
          label={t("search.filters.min")}
          onChange={(value) => commit([value, values[1]])}
        />
        <BoundInput
          value={values[1]}
          min={values[0]}
          max={high}
          unit={filter.def.unit}
          label={t("search.filters.max")}
          onChange={(value) => commit([values[0], value])}
        />
      </div>

      <div className="range-min-max mt-2">
        <Range
          values={values}
          min={low}
          max={high}
          onChange={(next) => commit(next)}
          onFinalChange={() => setDragging(null)}
          renderTrack={({ props, children }) => (
            <div
              className="line"
              onMouseDown={props.onMouseDown}
              onTouchStart={props.onTouchStart}
              style={props.style}
            >
              <div
                ref={props.ref}
                style={{
                  background: getTrackBackground({
                    values,
                    colors: [
                      configs.defaultTheme === "dark" ? "#ffffff" : "#000000",
                      configs.primaryColor ?? "var(--primary)",
                      configs.defaultTheme === "dark" ? "#ffffff" : "#000000",
                    ],
                    min: low,
                    max: high,
                  }),
                }}
              >
                {children}
              </div>
            </div>
          )}
          renderThumb={({ props, index }) => {
            const { key, ...rest } = props;
            return (
              <div
                key={key}
                {...rest}
                className="flex items-center justify-center rounded-full border-[6px] border-black dark:border-white"
                style={{
                  ...rest.style,
                  backgroundColor: configs.primaryColor ?? "var(--primary)",
                  width: 20,
                  height: 20,
                }}
                onMouseDown={() => setDragging(index)}
                onTouchStart={() => setDragging(index)}
              >
                {dragging === index && (
                  <span className="absolute top-[260%] whitespace-nowrap rounded bg-black px-2 py-1 text-[10px] font-semibold text-white">
                    {values[index].toLocaleString()}
                  </span>
                )}
              </div>
            );
          }}
        />
      </div>

      <button
        type="button"
        onClick={() => reset(filter.def.name)}
        aria-label={`${t("search.filters.clear")}: ${label}`}
        className="mt-2 cursor-pointer font-semibold hover:text-primary hidden tablet:inline-flex"
      >
        {t("search.filters.clear")}
      </button>
    </div>
  );
}

function BoundInput({
  value,
  min,
  max,
  unit,
  label,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  unit?: string;
  label: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="relative w-full">
      <input
        type="number"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        onChange={(event) => {
          const next = Number(event.target.value);
          // Out-of-order bounds would produce an empty result set and a slider
          // that can't be dragged back; clamping keeps both usable.
          if (Number.isFinite(next))
            onChange(Math.min(Math.max(next, min), max));
        }}
        className={cn(
          "w-full rounded border border-black p-1 ps-2 text-sm shadow-inner dark:text-black"
        )}
      />
      {unit && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-black/40">
          {unit}
        </span>
      )}
    </div>
  );
}
